import { yupResolver } from '@hookform/resolvers/yup'
import { format } from '@if/core/date'
import { ControlledDateOnlyPicker } from '@if/mui'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { InputTextField, SelectComponent } from '@src/components'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  useCreateTreeSimulation,
  useDecisionTreeVersions,
  useDecisionTrees,
} from '@src/data/api/tree-simulation/tree-simulation-api'
import { TreeSimulationFormSchema, TreeSimulationFormSchemaType } from '@src/data/types/TreeSimulationSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useDecisionTreeItems,
  useDecisionTreeItemsWithNone,
  useDecisionTreeVersionItems,
  useDecisionTreeVersionItemsWithNone,
  useFinancingProgramItems,
} from './tree-simulation-hooks'

const defaultValues = TreeSimulationFormSchema.getDefault() as TreeSimulationFormSchemaType

const NewTreeSimulationPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createSimulation, isCreating] = useCreateTreeSimulation()

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(TreeSimulationFormSchema),
    defaultValues,
    mode: 'onTouched',
  })

  const applicantTreeId = watch('applicantTreeId')
  const coapplicantTreeId = watch('coapplicantTreeId')

  const [trees] = useDecisionTrees()
  const [applicantVersions, applicantVersionsLoading] = useDecisionTreeVersions(applicantTreeId || null)
  const [coapplicantVersions, coapplicantVersionsLoading] = useDecisionTreeVersions(coapplicantTreeId || null)

  const financingProgramItems = useFinancingProgramItems()
  const treeItems = useDecisionTreeItems(trees)
  const treeItemsWithNone = useDecisionTreeItemsWithNone(trees)
  const applicantVersionItems = useDecisionTreeVersionItems(applicantVersions)
  const coapplicantVersionItems = useDecisionTreeVersionItemsWithNone(coapplicantVersions)

  const onSubmit = handleSubmit(async (data) => {
    try {
      await createSimulation({
        name: data.name,
        financingProgramId: data.financingProgramId,
        startDate: format(data.startDate, 'yyyy-MM-dd'),
        endDate: format(data.endDate, 'yyyy-MM-dd'),
        applicantTreeId: data.applicantTreeVersionId,
        coapplicantTreeId: data.coapplicantTreeVersionId ?? null,
      })
      navigate('/financial-engineer/tree-simulations')
    } catch (error) {
      reportErrorToConsole(error)
    }
  })

  const navigateBack = () => navigate('/financial-engineer/tree-simulations')

  return (
    <FullScreenContainer>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('treeSimulation.newSimulation')}
      </Typography>

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ maxWidth: 900 }}>
        <Grid container spacing={3}>
          {/* Row 1: Name | Financing Program */}
          <Grid size={{ xs: 12, md: 6 }}>
            <InputTextField
              label={t('treeSimulation.form.name')}
              required
              {...register('name')}
              error={errors.name}
              slotProps={{ htmlInput: { maxLength: 30 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectComponent
              items={financingProgramItems}
              label={t('treeSimulation.form.financingProgram')}
              {...register('financingProgramId')}
              error={errors.financingProgramId}
            />
          </Grid>

          {/* Row 2: Start Date | End Date */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledDateOnlyPicker
              name="startDate"
              control={control}
              label={t('treeSimulation.form.startDate')}
              sx={{ width: '100%' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledDateOnlyPicker
              name="endDate"
              control={control}
              label={t('treeSimulation.form.endDate')}
              sx={{ width: '100%' }}
            />
          </Grid>

          {/* Row 3: Applicant Tree | Applicant Tree Version */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectComponent
              items={treeItems}
              label={t('treeSimulation.form.applicantTree')}
              {...register('applicantTreeId')}
              error={errors.applicantTreeId}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectComponent
              items={applicantVersionItems}
              label={t('treeSimulation.form.applicantTreeVersion')}
              {...register('applicantTreeVersionId')}
              error={errors.applicantTreeVersionId}
              disabled={!applicantTreeId || applicantVersionsLoading}
            />
          </Grid>

          {/* Row 4: Coapplicant Tree | Coapplicant Tree Version */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectComponent
              items={treeItemsWithNone}
              label={t('treeSimulation.form.coapplicantTree')}
              {...register('coapplicantTreeId')}
              error={errors.coapplicantTreeId}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectComponent
              items={coapplicantVersionItems}
              label={t('treeSimulation.form.coapplicantTreeVersion')}
              {...register('coapplicantTreeVersionId')}
              error={errors.coapplicantTreeVersionId}
              disabled={!coapplicantTreeId || coapplicantVersionsLoading}
            />
          </Grid>

          {/* Actions */}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={2}>
              <Button type="submit" variant="contained" disabled={isCreating}>
                {t('treeSimulation.form.submit')}
              </Button>
              <Button variant="outlined" onClick={navigateBack}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FullScreenContainer>
  )
}

export default NewTreeSimulationPage
